// Styles for redesigned GeneralInformation section
// Created as part of ENG2-1711: UI Polish

import { SystemStyleObject, Theme } from '@mui/system'
import { tokens } from 'styles/tokens'

type StyleObject = SystemStyleObject<Theme>

export const styles = {
  // Main container
  container: {
    display: 'grid',
    gridTemplateColumns: '280px 1fr',
    gap: 3,
    p: 2.5,
    pb: 2,
    backgroundColor: 'background.paper',
    borderRadius: '12px',
  } as StyleObject,

  // Company info card (left side)
  companyCard: {
    backgroundColor: 'action.hover',
    borderRadius: '8px',
    p: 2.5,
    border: '1px solid',
    borderColor: 'divider',
  } as StyleObject,

  companyTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colors.grey[500],
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    mb: 2,
  } as StyleObject,

  infoItem: {
    mb: 2,
    '&:last-child': {
      mb: 0,
    },
  } as StyleObject,

  infoLabel: {
    fontSize: '12px',
    fontWeight: 500,
    color: tokens.colors.grey[500],
    mb: 0.5,
  } as StyleObject,

  infoValue: {
    fontSize: '14px',
    fontWeight: 500,
    color: 'text.primary',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  } as StyleObject,

  copyButton: {
    padding: '2px',
    color: tokens.colors.grey[400],
    '&:hover': {
      color: tokens.colors.primary[600],
      backgroundColor: 'transparent',
    },
  } as StyleObject,

  // Right side content
  rightContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  } as StyleObject,

  // Settings row (Plan, Responsible, Expiration)
  settingsRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 2,
    flexWrap: 'wrap',
  } as StyleObject,

  // Form field container
  fieldContainer: {
    minWidth: '160px',
    flex: '0 0 auto',
  } as StyleObject,

  fieldLabel: {
    fontSize: '12px',
    fontWeight: 500,
    color: tokens.colors.grey[600],
    mb: 0.75,
  } as StyleObject,

  // Consistent select styling
  select: {
    '& .MuiOutlinedInput-root': {
      height: '40px',
      fontSize: '14px',
      backgroundColor: 'background.paper',
      borderRadius: '8px',
      '& fieldset': {
        borderColor: 'divider',
      },
      '&:hover fieldset': {
        borderColor: 'text.disabled',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'primary.main',
        borderWidth: '1px',
      },
    },
    '& .MuiSelect-select': {
      padding: '8px 12px',
    },
  } as StyleObject,

  // Date picker styling
  datePicker: {
    '& .MuiOutlinedInput-root': {
      height: '40px',
      fontSize: '14px',
      backgroundColor: 'background.paper',
      borderRadius: '8px',
      '& fieldset': {
        borderColor: 'divider',
      },
      '&:hover fieldset': {
        borderColor: 'text.disabled',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'primary.main',
        borderWidth: '1px',
      },
    },
    '& .MuiInputBase-input': {
      padding: '8px 12px',
    },
  } as StyleObject,

  // Edit button
  editButton: {
    height: '40px',
    width: '40px',
    minWidth: '40px',
    borderRadius: '8px',
    border: '1px solid',
    borderColor: 'divider',
    color: 'text.secondary',
    backgroundColor: 'background.paper',
    '&:hover': {
      backgroundColor: 'action.hover',
      borderColor: 'text.disabled',
    },
  } as StyleObject,

  // Actions section
  actionsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  } as StyleObject,

  actionsSectionTitle: {
    fontSize: '12px',
    fontWeight: 500,
    color: tokens.colors.grey[500],
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  } as StyleObject,

  actionsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    flexWrap: 'wrap',
  } as StyleObject,

  // Consistent action button base style
  actionButton: {
    height: '36px',
    fontSize: '13px',
    fontWeight: 500,
    borderRadius: '8px',
    textTransform: 'none',
    padding: '0 14px',
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
  } as StyleObject,

  // Primary action (main actions)
  primaryAction: {
    backgroundColor: tokens.colors.primary[600],
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: tokens.colors.primary[700],
    },
    '&:disabled': {
      backgroundColor: tokens.colors.grey[200],
      color: tokens.colors.grey[400],
    },
  } as StyleObject,

  // Secondary action (neutral actions)
  secondaryAction: {
    backgroundColor: 'background.paper',
    color: 'text.primary',
    border: '1px solid',
    borderColor: 'divider',
    '&:hover': {
      backgroundColor: 'action.hover',
      borderColor: 'text.disabled',
    },
  } as StyleObject,

  // Success action (positive actions like copy, grant)
  successAction: {
    backgroundColor: tokens.colors.success[600],
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: tokens.colors.success[700],
    },
  } as StyleObject,

  // Warning action (caution actions like revoke)
  warningAction: {
    backgroundColor: tokens.colors.warning[600],
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: tokens.colors.warning[700],
    },
  } as StyleObject,

  // Danger action (destructive actions)
  dangerAction: {
    backgroundColor: tokens.colors.error[600],
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: tokens.colors.error[700],
    },
  } as StyleObject,

  // Ghost/text action
  ghostAction: {
    backgroundColor: 'transparent',
    color: 'text.secondary',
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  } as StyleObject,

  // Integration section
  integrationSection: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 1.5,
    pt: 1,
    borderTop: '1px solid',
    borderColor: 'divider',
  } as StyleObject,

  integrationInput: {
    flex: 1,
    maxWidth: '280px',
    '& .MuiOutlinedInput-root': {
      height: '36px',
      fontSize: '13px',
      backgroundColor: 'background.paper',
      borderRadius: '8px',
      '& fieldset': {
        borderColor: 'divider',
      },
    },
    '& .MuiInputBase-input': {
      padding: '8px 12px',
    },
  } as StyleObject,

  // Icon styling
  buttonIcon: {
    fontSize: '14px',
    marginRight: '6px',
  },
}
